<template>
  <div>
    <div
      v-if="visible"
      class="payment-method-modal-overlay">
      <div
        role="dialog"
        aria-describedby="payment-method-modal-description"
        aria-labelledby="payment-method-modal-title"
        class="payment-method-modal-dialog"
        :class="{ 'payment-method-modal-dialog-confirming': showCloseConfirmation }">
        <div class="payment-method-modal-header">
          <div class="payment-method-modal-header-content">
            <h2 id="payment-method-modal-title" class="payment-method-modal-title">
              Forma de Pagamento
            </h2>
            <p id="payment-method-modal-description" class="payment-method-modal-subtitle">
              Selecione como deseja pagar e confirme os dados do contato financeiro.
            </p>
          </div>
          <button
            type="button"
            class="payment-method-modal-close"
            aria-label="Fechar"
            :disabled="loading"
            @click="requestClose">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M18 6 6 18"></path>
              <path d="m6 6 12 12"></path>
            </svg>
          </button>
        </div>

        <div v-if="hasPendingInvoice" class="payment-method-pending-disclaimer">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="payment-method-pending-icon" aria-hidden="true">
            <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"></path>
            <line x1="12" x2="12" y1="9" y2="13"></line>
            <line x1="12" x2="12.01" y1="17" y2="17"></line>
          </svg>
          <p class="payment-method-pending-text">
            Identificamos uma
            <a
              v-if="pendingInvoiceData.externalUrl"
              :href="pendingInvoiceData.externalUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="payment-method-pending-link">cobrança já criada aguardando pagamento
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="payment-method-pending-link-icon">
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                <polyline points="15 3 21 3 21 9"></polyline>
                <line x1="10" y1="14" x2="21" y2="3"></line>
              </svg></a><span v-else>cobrança já criada aguardando pagamento</span>.
            Ao continuar, ela será cancelada e uma nova cobrança será gerada com as informações atualizadas.
          </p>
        </div>

        <div class="payment-method-modal-body">
          <div
            v-if="loading"
            class="payment-method-loading-overlay"
            aria-live="polite"
            aria-busy="true">
            <div class="payment-method-loading-content">
              <div class="payment-method-spinner">
                <svg class="payment-method-spinner-svg" viewBox="0 0 50 50" aria-hidden="true">
                  <circle class="payment-method-spinner-bg" cx="25" cy="25" r="20" fill="none" stroke-width="4" />
                  <circle class="payment-method-spinner-arc" cx="25" cy="25" r="20" fill="none" stroke-width="4" />
                </svg>
              </div>
              <p class="payment-method-loading-label">{{ loadingLabel }}</p>
            </div>
          </div>
          <div class="payment-method-section">
            <h3 class="payment-method-section-title">1. Como deseja pagar?</h3>
            <div class="payment-method-options">
              <button
                type="button"
                class="payment-option-card"
                :class="{ 'payment-option-card-selected': selectedPaymentMethod === 'credit_card' }"
                @click="selectPaymentMethod('credit_card')">
                <span class="payment-option-radio">
                  <span v-if="selectedPaymentMethod === 'credit_card'" class="payment-option-radio-dot"></span>
                </span>
                <span class="payment-option-icon-wrap">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="payment-option-icon">
                    <rect width="20" height="14" x="2" y="5" rx="2"></rect>
                    <line x1="2" x2="22" y1="10" y2="10"></line>
                  </svg>
                </span>
                <span class="payment-option-content">
                  <span class="payment-option-label">Cartão de crédito</span>
                  <span class="payment-option-hint">Instantâneo</span>
                </span>
              </button>

              <button
                type="button"
                class="payment-option-card"
                :class="{ 'payment-option-card-selected': selectedPaymentMethod === 'pix' }"
                @click="selectPaymentMethod('pix')">
                <span class="payment-option-radio">
                  <span v-if="selectedPaymentMethod === 'pix'" class="payment-option-radio-dot"></span>
                </span>
                <span class="payment-option-icon-wrap">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="payment-option-icon payment-option-icon-qr" aria-hidden="true">
                    <path d="M4 4h6v6H4V4Zm10 10h6v6h-6v-6Zm0-10h6v6h-6V4Zm-4 10h.01v.01H10V14Zm0 4h.01v.01H10V18Zm-3 2h.01v.01H7V20Zm0-4h.01v.01H7V16Zm-3 2h.01v.01H4V18Zm0-4h.01v.01H4V14Z"/>
                    <path d="M7 7h.01v.01H7V7Zm10 10h.01v.01H17V17Z"/>
                  </svg>
                </span>
                <span class="payment-option-content">
                  <span class="payment-option-label">PIX</span>
                  <span class="payment-option-hint">Até 24h</span>
                </span>
              </button>

              <button
                type="button"
                class="payment-option-card"
                :class="{ 'payment-option-card-selected': selectedPaymentMethod === 'boleto_pix' }"
                @click="selectPaymentMethod('boleto_pix')">
                <span class="payment-option-radio">
                  <span v-if="selectedPaymentMethod === 'boleto_pix'" class="payment-option-radio-dot"></span>
                </span>
                <span class="payment-option-icon-wrap">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="payment-option-icon lucide lucide-banknote">
                    <rect width="20" height="12" x="2" y="6" rx="2"></rect>
                    <circle cx="12" cy="12" r="2"></circle>
                    <path d="M6 12h.01M18 12h.01"></path>
                  </svg>
                </span>
                <span class="payment-option-content">
                  <span class="payment-option-label">Boleto bancário / PIX</span>
                  <span class="payment-option-hint">Até 3 dias úteis</span>
                </span>
              </button>
            </div>
          </div>

          <div class="payment-method-section">
            <h3 class="payment-method-section-title">
              2. Contato financeiro
              <span class="payment-method-info-badge-wrap">
                <span class="payment-method-info-badge">i</span>
                <span class="payment-method-info-badge-card">
                  Esses contatos receberão notificações financeiras como envio de cobranças, confirmação de pagamento, notas fiscais e comprovantes.
                </span>
              </span>
            </h3>
            <div class="payment-method-contact-grid">
              <div class="payment-method-field">
                <label class="payment-method-field-label">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="payment-method-field-icon">
                    <rect width="20" height="16" x="2" y="4" rx="2"></rect>
                    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"></path>
                  </svg>
                  E-mail
                </label>
                <input
                  v-model="financeEmails"
                  type="text"
                  class="payment-method-input"
                  placeholder="financeiro@suaempresa.com.br">
                <p class="payment-method-email-hint">
                  Até 5 e-mails separados por vírgula.
                </p>
              </div>

              <div class="payment-method-field">
                <label class="payment-method-field-label">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="payment-method-field-icon">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92Z"></path>
                  </svg>
                  Telefone
                </label>
                <input
                  v-model="contactPhone"
                  type="tel"
                  class="payment-method-input"
                  :class="{ 'payment-method-input-error': contactPhone && !isContactPhoneValid }"
                  @keydown="allowOnlyNumericKeydown($event)"
                  @input="onContactPhoneInput"
                  placeholder="(11) 99999-9999">
                <p v-if="contactPhone && !isContactPhoneValid" class="payment-method-field-error">
                  Insira um telefone válido
                </p>
              </div>
            </div>
          </div>

          <div v-if="showSubscriptionPaymentNote" class="payment-method-info-box">
            <svg class="payment-method-info-icon" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 24 24">
              <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 11h2v5m-2 0h4m-2.592-8.5h.01M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/>
            </svg>
            <div class="payment-method-info-text">
              <p>
                As próximas cobranças usarão <strong>{{ selectedPaymentMethodLabel }}</strong>. Para alterar depois, fale com <a href="https://wa.me/+554833328503" target="_blank" rel="noopener noreferrer" class="payment-method-support-link">nosso suporte</a>.
              </p>
            </div>
          </div>
        </div>

        <div class="payment-method-modal-footer">
          <p v-if="displayError" class="payment-method-error-text">{{ displayError }}</p>
          <div class="payment-method-modal-actions">
            <button
              type="button"
              class="payment-method-cancel-button"
              :disabled="loading"
              @click="handleClose">
              Cancelar
            </button>
            <button
              type="button"
              class="payment-method-confirm-button"
              :disabled="loading || !isContactPhoneValid"
              @click="handleConfirm">
              <span v-if="loading">Gerando cobrança...</span>
              <span v-else>Confirmar e Gerar Cobrança</span>
            </button>
          </div>
        </div>

        <div
          v-if="showCloseConfirmation"
          role="alertdialog"
          aria-labelledby="payment-method-confirm-title"
          aria-describedby="payment-method-confirm-description"
          class="payment-method-confirm">
          <h3 id="payment-method-confirm-title" class="payment-method-confirm-title">
            Tem certeza que deseja interromper seu upgrade?
          </h3>
          <p id="payment-method-confirm-description" class="payment-method-confirm-text">
            Se você sair agora, seu upgrade não será concluído.
          </p>
          <div class="payment-method-confirm-actions">
            <button
              type="button"
              class="payment-method-confirm-stay"
              @click="cancelClose">
              Continuar upgrade
            </button>
            <button
              type="button"
              class="payment-method-confirm-leave"
              @click="confirmClose">
              Sim, interromper
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import loopback from '@/services/loopback'
import { formatPhoneMask, phoneDigitsOnly, allowOnlyNumericKeydown, isValidPhone, maskNationalPhone } from '@/utils/phone'
import { triggerUserDataUpdateWebhook } from '@/services/user-data-update-webhook'

export default {
  name: 'PaymentMethodModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    subscriptionError: {
      type: String,
      default: null
    },
    loadingLabel: {
      type: String,
      default: 'Gerando cobrança...'
    },
    pendingInvoiceData: {
      type: Object,
      default: null
    },
    // Só a contratação define a forma de pagamento da assinatura. Uma cobrança avulsa
    // (compra de licenças) não muda as próximas cobranças.
    showSubscriptionPaymentNote: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      selectedPaymentMethod: 'credit_card',
      financeEmails: '',
      emailError: null,
      showCloseConfirmation: false,
      contactPhone: '',
      phoneError: null
    }
  },
  computed: {
    hasPendingInvoice () {
      return this.pendingInvoiceData && this.pendingInvoiceData.total > 0
    },
    selectedPaymentMethodLabel () {
      const labels = {
        credit_card: 'Cartão de crédito',
        boleto_pix: 'Boleto bancário / PIX',
        pix: 'PIX'
      }
      return labels[this.selectedPaymentMethod]
    },
    financialData () {
      return this.$store.state.auth.financialData
    },
    isContactPhoneValid () {
      return isValidPhone(this.contactPhone)
    },
    displayError () {
      if (this.subscriptionError && typeof this.subscriptionError === 'string' && this.subscriptionError.trim() !== '') {
        return this.subscriptionError
      }
      if (this.emailError && typeof this.emailError === 'string' && this.emailError.trim() !== '') {
        return this.emailError
      }
      if (this.phoneError && typeof this.phoneError === 'string' && this.phoneError.trim() !== '') {
        return this.phoneError
      }
      return null
    }
  },
  watch: {
    visible (isVisible) {
      this.showCloseConfirmation = false
      if (isVisible) {
        this.syncFinanceEmailsFromFinancialData()
        this.syncContactPhoneFromUser()
        this.emailError = null
        this.phoneError = null
      }
    }
  },
  methods: {
    allowOnlyNumericKeydown,
    syncFinanceEmailsFromFinancialData () {
      const financialData = this.financialData
      if (financialData && financialData.additionalEmails != null) {
        this.financeEmails = financialData.additionalEmails
      } else {
        this.financeEmails = ''
      }
    },
    requestClose () {
      this.showCloseConfirmation = true
    },
    cancelClose () {
      this.showCloseConfirmation = false
    },
    confirmClose () {
      this.showCloseConfirmation = false
      this.$emit('close')
    },
    syncContactPhoneFromUser () {
      this.contactPhone = maskNationalPhone(this.$store.state.auth.user.phone)
    },
    onContactPhoneInput () {
      this.contactPhone = formatPhoneMask(phoneDigitsOnly(this.contactPhone))
    },
    handleClose () {
      this.$emit('close')
    },
    async handleConfirm () {
      this.emailError = null
      this.phoneError = null
      if (!this.isContactPhoneValid) return
      try {
        await this.persistFinanceEmailsIfDirty()
      } catch (err) {
        const message = typeof err.message === 'string' ? err.message : ''
        if (message.includes('additionalEmails: invalid')) {
          this.emailError = message.includes('maximum 5 emails')
            ? 'Máximo de 5 emails adicionais.'
            : 'Emails adicionais inválidos. Exemplo: "contato@dominio.com,financeiro@dominio.com"'
        } else {
          // TEL-2243: `err.message` e texto do backend e nao vai para a tela — num 5xx
          // do PUT /accounts/financial isso renderizava "Internal Server Error" embaixo
          // do campo de e-mail. O `if` acima segue lendo a `message` de proposito: ela
          // nunca e sobrescrita, e e o que distingue os dois erros de additionalEmails.
          this.emailError = 'Não foi possível atualizar os e-mails. Tente novamente em alguns minutos.'
        }
        return
      }
      try {
        await this.persistContactPhoneIfChanged()
      } catch (err) {
        this.phoneError = 'Não foi possível atualizar o telefone.'
        return
      }
      this.$emit('confirm', {
        selectedPaymentMethod: this.selectedPaymentMethod,
        financeEmails: this.financeEmails
      })
    },
    async persistFinanceEmailsIfDirty () {
      const currentEmails = this.financeEmails != null ? String(this.financeEmails).trim() : ''
      const storedEmails = this.financialData && this.financialData.additionalEmails != null
        ? String(this.financialData.additionalEmails).trim()
        : ''
      const isDirty = this.financialData && currentEmails !== storedEmails
      if (!isDirty) return
      const updated = await loopback.put('/accounts/financial', { additionalEmails: this.financeEmails })
      if (updated) this.$store.commit('auth/setFinancialData', updated)
    },
    async persistContactPhoneIfChanged () {
      const digits = phoneDigitsOnly(this.contactPhone)
      const currentUser = this.$store.state.auth.user
      const previousPhone = phoneDigitsOnly(currentUser && currentUser.phone)
      if (digits === previousPhone) return
      // Envia o email do usuário LOGADO para o PATCH atualizar o próprio usuário
      // (isSelfUpdate). Sem email, o backend cai no 1º usuário da org (founder) e o
      // setUser trocaria a sessão.
      const user = await loopback.patch('/users', {
        email: currentUser && currentUser.email,
        phone: digits
      })
      this.$store.commit('auth/setUser', user)
      triggerUserDataUpdateWebhook({
        email: user.email,
        name: user.name,
        phone: user.phone,
        domain: this.$store.state.auth.organization.domain
      })
    },
    selectPaymentMethod (value) {
      this.selectedPaymentMethod = value
    }
  }
}
</script>

<style scoped>
.payment-method-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.5);
}

.payment-method-modal-dialog {
  pointer-events: auto;
  max-width: 38rem;
  width: 90%;
  background: #ffffff;
  border-radius: 0.75rem;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  overflow: visible;
  position: relative;
}

.payment-method-modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 1rem 1.5rem 0 1.5rem;
  position: relative;
}

.payment-method-modal-header-content {
  flex: 1;
  text-align: left;
  padding-right: 2rem;
}

.payment-method-modal-title {
  font-size: 1.125rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 0.125rem 0;
}

.payment-method-modal-subtitle {
  font-size: 0.8125rem;
  color: #6b7280;
  margin: 0;
}

.payment-method-modal-close {
  position: absolute;
  top: 1rem;
  right: 1.5rem;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0.25rem;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
}

.payment-method-modal-close:hover {
  color: #374151;
}

.payment-method-modal-body {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  padding: 1.125rem 1.5rem 1rem 1.5rem;
  position: relative;
}

.payment-method-section {
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
}

.payment-method-section-title {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.9375rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
}

.payment-method-loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  border-radius: 0 0 0.75rem 0.75rem;
}

.payment-method-loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.payment-method-spinner {
  width: 48px;
  height: 48px;
  animation: payment-method-spinner-rotate 0.85s linear infinite;
}

.payment-method-spinner-svg {
  width: 100%;
  height: 100%;
  display: block;
}

.payment-method-spinner-bg {
  stroke: #e5e7eb;
  stroke-linecap: round;
}

.payment-method-spinner-arc {
  stroke: #16a34a;
  stroke-linecap: round;
  stroke-dasharray: 90, 150;
  stroke-dashoffset: 0;
}

.payment-method-loading-label {
  font-size: 0.9375rem;
  font-weight: 500;
  color: #374151;
  margin: 0;
}

@keyframes payment-method-spinner-rotate {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.payment-method-options {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.625rem;
}

.payment-option-card {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem 0.625rem 0.75rem;
  border-radius: 0.75rem;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  cursor: pointer;
  text-align: center;
  transition: border-color 0.2s, background-color 0.2s;
}

.payment-option-card:hover {
  border-color: #d1d5db;
}

.payment-option-card-selected {
  border-color: #16a34a;
  background-color: #f0fdf4;
}

.payment-option-radio {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  width: 1.125rem;
  height: 1.125rem;
  border: 2px solid #7c3aed;
  border-radius: 50%;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.payment-option-radio-dot {
  width: 0.375rem;
  height: 0.375rem;
  border-radius: 50%;
  background-color: #7c3aed;
}

.payment-option-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.125rem;
  min-width: 0;
}

.payment-option-icon-wrap {
  flex-shrink: 0;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  background-color: #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
}

.payment-option-icon {
  color: #4b5563;
  width: 1rem;
  height: 1rem;
}

.payment-option-label {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #1f2937;
  line-height: 1.2;
}

.payment-option-hint {
  font-size: 0.6875rem;
  color: #6b7280;
  line-height: 1.2;
}

.payment-method-contact-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.payment-method-field {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

.payment-method-field-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: #1f2937;
  margin: 0;
}

.payment-method-field-icon {
  color: #6b7280;
  flex-shrink: 0;
}

.payment-method-field-error {
  font-size: 0.75rem;
  color: #dc2626;
  margin: 0;
}

.payment-method-info-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1rem;
  height: 1rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: #2563eb;
  background: #dbeafe;
  border-radius: 50%;
  cursor: help;
}

.payment-method-info-badge-wrap {
  position: relative;
  display: inline-flex;
}

.payment-method-info-badge-wrap .payment-method-info-badge-card {
  position: absolute;
  left: 50%;
  bottom: calc(100% + 0.5rem);
  transform: translateX(-50%);
  visibility: hidden;
  opacity: 0;
  transition: visibility 0.15s, opacity 0.15s;
  z-index: 10;
  width: 22rem;
  max-width: min(22rem, calc(100vw - 2rem));
  padding: 0.625rem 0.875rem;
  font-size: 0.8125rem;
  font-weight: 400;
  color: #374151;
  line-height: 1.5;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.06);
  pointer-events: none;
  box-sizing: border-box;
}

.payment-method-info-badge-wrap .payment-method-info-badge-card::after {
  content: "";
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  border: 4px solid transparent;
  border-top-color: #ffffff;
}

.payment-method-info-badge-wrap:hover .payment-method-info-badge-card {
  visibility: visible;
  opacity: 1;
}

.payment-method-input {
  width: 100%;
  padding: 0.425rem 0.875rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  color: #1f2937;
  background: #ffffff;
  box-sizing: border-box;
}

.payment-method-input:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.2);
}

.payment-method-input-error {
  border-color: #dc2626;
}

.payment-method-input-error:focus {
  border-color: #dc2626;
  box-shadow: 0 0 0 2px rgba(220, 38, 38, 0.2);
}

.payment-method-email-hint {
  font-size: 0.75rem;
  color: #9ca3af;
  margin: 0;
}

.payment-method-info-box {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 0.5rem;
  margin-top: 0;
}

.payment-method-info-icon {
  flex-shrink: 0;
  color: #2563eb;
}

.payment-method-info-text {
  font-size: 0.6875rem;
  color: #2563eb;
  line-height: 1.35;
  margin: 0;
  font-family: ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
}

.payment-method-info-text p {
  margin: 0 0 0.0625rem 0;
}

.payment-method-info-text p:last-child {
  margin-bottom: 0;
}

.payment-method-info-text strong {
  color: #2563eb;
  font-weight: 600;
}

.payment-method-support-link {
  color: #2563eb;
  text-decoration: none;
}

.payment-method-support-link:hover {
  color: #1d4ed8;
  text-decoration: underline;
}

.payment-method-modal-footer {
  padding: 0 1.5rem 1rem 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.5rem;
  justify-content: center;
}

.payment-method-error-text {
  font-size: 0.875rem;
  color: #dc2626;
  margin: 0;
  text-align: center;
}

.payment-method-modal-actions {
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
}

.payment-method-cancel-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: 0.625rem 1.25rem;
  font-size: 0.9375rem;
  font-weight: 600;
  color: #374151;
  background-color: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: background-color 0.2s;
}

.payment-method-cancel-button:hover {
  background-color: #f3f4f6;
}

.payment-method-cancel-button:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.payment-method-confirm-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: 0.625rem 1.25rem;
  font-size: 0.9375rem;
  font-weight: 600;
  color: #ffffff;
  background-color: #16a34a;
  border: none;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: background-color 0.2s;
}

.payment-method-confirm-button:hover {
  background-color: #15803d;
}

.payment-method-confirm-button:disabled {
  background-color: #9ca3af;
  cursor: not-allowed;
  opacity: 0.9;
}

.payment-method-modal-dialog-confirming {
  max-width: 26rem;
}

.payment-method-modal-dialog-confirming > *:not(.payment-method-confirm) {
  display: none;
}

.payment-method-confirm {
  padding: 1.5rem;
  text-align: center;
}

.payment-method-confirm-title {
  font-size: 1.125rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 0.5rem 0;
}

.payment-method-confirm-text {
  font-size: 0.875rem;
  color: #6b7280;
  line-height: 1.5;
  margin: 0 0 1.5rem 0;
}

.payment-method-confirm-actions {
  display: flex;
  gap: 0.75rem;
  justify-content: center;
}

.payment-method-confirm-stay {
  flex: 1;
  padding: 0.625rem 1rem;
  font-size: 0.9375rem;
  font-weight: 600;
  color: #ffffff;
  background-color: #16a34a;
  border: none;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: background-color 0.2s;
}

.payment-method-confirm-stay:hover {
  background-color: #15803d;
}

.payment-method-confirm-leave {
  flex: 1;
  padding: 0.625rem 1rem;
  font-size: 0.9375rem;
  font-weight: 600;
  color: #374151;
  background-color: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: background-color 0.2s, border-color 0.2s;
}

.payment-method-confirm-leave:hover {
  background-color: #f9fafb;
  border-color: #9ca3af;
}

.payment-method-pending-disclaimer {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.625rem 1rem;
  margin: 0.75rem 1.5rem 0 1.5rem;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 0.5rem;
}

.payment-method-pending-icon {
  flex-shrink: 0;
  color: #d97706;
  margin-top: 0.125rem;
}

.payment-method-pending-text {
  font-size: 0.8125rem;
  color: #92400e;
  line-height: 1.4;
  margin: 0;
}

.payment-method-pending-link {
  color: #92400e;
  font-weight: 600;
  text-decoration: underline;
}

.payment-method-pending-link:hover {
  color: #78350f;
}

.payment-method-pending-link-icon {
  display: inline;
  vertical-align: middle;
  margin-left: 0.125rem;
}

@media (max-width: 640px) {
  .payment-method-options {
    grid-template-columns: 1fr;
  }

  .payment-method-contact-grid {
    grid-template-columns: 1fr;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/Subscription/PaymentMethodModal.vue